/**
 * Salesforce Embedded Messaging Web (ESW) — load `bootstrap.min.js`, call
 * `embeddedservice_bootstrap.init`, apply JWT in `onEmbeddedMessagingReady`.
 * Matches the standard Experience Cloud embed pattern (see deployment site URL + scrt2URL).
 */

import moment from 'moment'

import { APP_VERSION } from '@/lib/consts'
import { getSalesforceInAppChatConfigForEnv } from '@/composables/chat/providers/agentforce/setup-agentforce-chat'

const BOOTSTRAP_SCRIPT_PATH = '/assets/js/bootstrap.min.js'
const SCRIPT_ATTR = 'data-booksy-agentforce-esw'

/** `yyyy-MM-dd'T'HH:mm:ss.SSS±HHmm` (e.g. `2026-05-05T14:24:04.333+0200`). Moment token `ZZ` → `±HHmm`. */
function formatCurrentDateTimeWithNumericOffset (date: Date): string {
  return moment(date).format('YYYY-MM-DDTHH:mm:ss.SSSZZ')
}

/** Single-tag locales where Salesforce expects an explicit region (BCP 47, hyphen). */
const APP_LOCALE_TO_SALESFORCE: Record<string, string> = {
  en: 'en-US',
  pt: 'pt-BR',
  zh: 'zh-CN',
}

/**
 * Maps Frontdesk Vuex `app.locale` (see {@link getCurrentLocale}) to Salesforce
 * `endUserLanguage` / ESW language: BCP 47 with hyphen (`en-US`, `de`, `pl`, …).
 */
export function mapAppLocaleToSalesforceLanguage (locale: string | undefined | null): string {
  if (locale == null || typeof locale !== 'string') {
    return 'en-US'
  }
  const key = locale.trim().toLowerCase()
  if (!key) {
    return 'en-US'
  }
  const mapped = APP_LOCALE_TO_SALESFORCE[key]
  if (mapped) {
    return mapped
  }
  const parts = key.split('-')
  if (parts.length >= 2) {
    const [language, region] = parts
    return `${language}-${region.toUpperCase()}`
  }
  return key
}

/**
 * Hidden pre-chat field map for Salesforce ESW. Keys come from {@link getSalesforceInAppChatConfigForEnv}
 * and must match hidden field API names on the Embedded Service deployment.
 *
 * `endUserLanguage` must match values configured in SF (BCP 47: `en-US`, `de`, `pl`, …).
 *
 * `businessProviderId` / `userId`: same prefixed IDs as analytics ({@link getExternalId}), e.g. `pl-68168`.
 *
 * `deviceType` / `appCountry`: same as analytics ({@link getDeviceType}, routing / app country).
 * `appCountry` is ISO 3166-1 alpha-2 uppercase (`PL`, `US`, …).
 */
export function getBooksyHiddenPrechatFields (params: {
  endUserLanguage: string
  businessProviderId: string
  userId: string
  deviceType: string
  appCountry: string
}): Record<string, string> {
  const k = getSalesforceInAppChatConfigForEnv().prechatFieldKeys
  return {
    [k.endUserLanguage]: params.endUserLanguage,
    [k.deviceType]: params.deviceType,
    [k.appCountry]: params.appCountry,
    [k.businessProviderId]: params.businessProviderId,
    [k.userId]: params.userId,
    /** Same {@link APP_VERSION} as analytics / GTM (`app_version`). */
    [k.appVersion]: `Frontdesk ${APP_VERSION}`,
    [k.currentDateTime]: formatCurrentDateTimeWithNumericOffset(new Date()),
  }
}

export type VisiblePrechatFieldPayload = {
  value: string
  isEditableByEndUser?: boolean
}

export type EmbeddedMessagingHandles = {
  /** Resolves after the iframe messaging client finishes init (`onEmbeddedMessagingInitSuccess`). Required before sending messages or the API can return 417 "No existing session found". */
  launchChat: () => Promise<void>
  hideEmbeddedMessaging: () => void
}

type EmbeddedServiceBootstrap = {
  settings: { language: string }
  prechatAPI: {
    setVisiblePrechatFields: (fields: Record<string, VisiblePrechatFieldPayload>) => void
    setHiddenPrechatFields: (fields: Record<string, string>) => void
  }
  userVerificationAPI: {
    setIdentityToken: (params: { identityTokenType: string; identityToken: string }) => void
  }
  init: (
    organizationId: string,
    deploymentDeveloperName: string,
    siteUrl: string,
    options: { scrt2URL: string },
  ) => void
  utilAPI: {
    launchChat: () => Promise<unknown>
    hideEmbeddedMessaging?: () => void
  }
}

declare global {
  interface Window {
    embeddedservice_bootstrap?: EmbeddedServiceBootstrap
  }
}

function getBootstrap (): EmbeddedServiceBootstrap {
  if (typeof window === 'undefined' || !window.embeddedservice_bootstrap) {
    throw new Error('Embedded Messaging bootstrap is not available (embeddedservice_bootstrap)')
  }
  return window.embeddedservice_bootstrap
}

export function removeEmbeddedMessagingBootstrapScript (): void {
  if (typeof document === 'undefined') {
    return
  }
  document.querySelectorAll(`script[${SCRIPT_ATTR}]`).forEach((el) => el.remove())
}

/** Best-effort teardown so a new deployment URL / JWT session can re-init in the same SPA tab. */
export function teardownEmbeddedMessagingBootstrap (): void {
  removeEmbeddedMessagingBootstrapScript()
  try {
    Reflect.deleteProperty(window as Window & { embeddedservice_bootstrap?: EmbeddedServiceBootstrap }, 'embeddedservice_bootstrap')
  } catch {
    /* non-configurable global — ignore */
  }
}

function appendBootstrapScript (src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = src
    script.async = true
    script.setAttribute(SCRIPT_ATTR, '1')
    script.onload = (): void => resolve()
    script.onerror = (): void =>
      reject(new Error(`Failed to load Embedded Messaging bootstrap script: ${src}`))
    document.head.appendChild(script)
  })
}

/**
 * Loads `/assets/js/bootstrap.min.js` from the Experience Cloud deployment base URL.
 */
export async function loadEmbeddedMessagingBootstrapScript (siteBaseUrl: string): Promise<void> {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return
  }

  const base = siteBaseUrl.replace(/\/+$/, '')
  const src = `${base}${BOOTSTRAP_SCRIPT_PATH}`

  console.log('loadEmbeddedMessagingBootstrapScript', src)

  const existing = document.querySelector<HTMLScriptElement>(`script[${SCRIPT_ATTR}]`)
  if (existing?.src === src && window.embeddedservice_bootstrap) {
    return
  }

  if (existing && existing.src !== src) {
    teardownEmbeddedMessagingBootstrap()
  }

  await appendBootstrapScript(src)

  const deadline = Date.now() + 15_000
  while (Date.now() < deadline) {
    if (window.embeddedservice_bootstrap) {
      return
    }
    await new Promise((r) => setTimeout(r, 50))
  }

  throw new Error('Embedded Messaging bootstrap script loaded but embeddedservice_bootstrap was not defined')
}

export async function startEmbeddedMessagingSession (params: {
  siteBaseUrl: string
  organizationId: string
  deploymentDeveloperName: string
  scrt2URL: string
  jwt: string
  language?: string
  /** Hidden pre-chat field; defaults to the same value as `language` when omitted. */
  endUserLanguage?: string
  /** {@link getExternalId} for current business; hidden pre-chat. */
  businessProviderId?: string
  /** {@link getExternalId} for current user; hidden pre-chat. */
  userId?: string
  /** {@link getDeviceType} for current session (Desktop / iOS / Android / Tablet). */
  deviceType?: string
  /** Uppercase market code from routing config or `app.country`. */
  appCountry?: string
}): Promise<EmbeddedMessagingHandles> {
  await loadEmbeddedMessagingBootstrapScript(params.siteBaseUrl)

  const bs = getBootstrap()
  const language = params.language ?? 'en-US'
  bs.settings.language = language

  await new Promise<void>((resolve, reject) => {
    const onReady = (): void => {
      window.removeEventListener('onEmbeddedMessagingReady', onReady)
      try {
        if (params.jwt && bs.userVerificationAPI) {
          bs.userVerificationAPI.setIdentityToken({
            identityTokenType: 'JWT',
            identityToken: params.jwt,
          })
        }

        bs.prechatAPI?.setHiddenPrechatFields(
          getBooksyHiddenPrechatFields({
            endUserLanguage: params.endUserLanguage ?? language,
            businessProviderId: params.businessProviderId ?? '',
            userId: params.userId ?? '',
            deviceType: params.deviceType ?? '',
            appCountry: params.appCountry ?? '',
          })
        )
        resolve()
      } catch (e) {
        reject(e instanceof Error ? e : new Error(String(e)))
      }
    }

    window.addEventListener('onEmbeddedMessagingReady', onReady)

    try {
      console.log('bs.init', params.organizationId, params.deploymentDeveloperName, params.siteBaseUrl.replace(/\/+$/, ''), { scrt2URL: params.scrt2URL })

      bs.init(
        params.organizationId,
        params.deploymentDeveloperName,
        params.siteBaseUrl.replace(/\/+$/, ''),
        { scrt2URL: params.scrt2URL }
      )
    } catch (e) {
      window.removeEventListener('onEmbeddedMessagingReady', onReady)
      reject(e instanceof Error ? e : new Error(String(e)))
    }
  })

  const api = getBootstrap().utilAPI

  return {
    launchChat: (): Promise<void> =>
      Promise.resolve(api.launchChat()).then(() => {}),
    hideEmbeddedMessaging: (): void => {
      if (typeof api.hideEmbeddedMessaging === 'function') {
        api.hideEmbeddedMessaging()
      }
    },
  }
}
